<template>
    <base-panel
        :panel="panel"
        :review-mode="reviewMode"
        v-on:continue="handleContinue()"
        v-on:submit="handleSubmit()"
        v-on:edit-action="handleEditAction($event)"
        v-on:update-width="handleUpdateWidth($event)"
        ref="basePanel"
    >
        <template slot="content">
            <textarea
                :id="'text-area-' + panelID"
                v-model="textResponse"
                @input="saveResponse($event.target.value)"
                :maxlength="panel.character_limit || 5000"
                :placeholder="panel.textarea_placeholder || labelsTranslated.typeYourResponse"
                class="form-control"
                :class="{ textarea: fillPanelHeight, 'text-secondary': isDisabled }"
                :rows="panel.textarea_rows || 10"
                :disabled="isDisabled"
                aria-label="Your response"
                dir="auto"
            ></textarea>
            <div v-show="textResponseEntities.length > 0" class="mt-1">
                <div
                    v-for="textEntity in textResponseEntities"
                    :key="textEntity.id"
                    class="alert py-2 px-3 mt-2 mb-0"
                    :class="{
                        'alert-success': textEntity.was_discovered && textEntity.correct === true,
                        'alert-warning': !textEntity.was_discovered && textEntity.correct === true,
                        'alert-danger': textEntity.was_discovered && textEntity.correct === false,
                        'alert-info': textEntity.was_discovered && textEntity.correct === null,
                        'd-none': hideEntity(textEntity),
                    }"
                >
                    <span
                        v-show="textEntity.was_discovered && textEntity.correct === true"
                        class="icon icon-glyphicons-basic-circle-empty-check align-middle icon-mr"
                    ></span>
                    <span
                        v-show="textEntity.was_discovered && textEntity.correct === false"
                        class="icon icon-glyphicons-basic-circle-empty-remove align-middle icon-mr"
                    ></span>
                    <span
                        v-show="!textEntity.was_discovered && textEntity.correct === true"
                        class="icon icon-glyphicons-basic-circle-empty-alert align-middle icon-mr"
                    ></span>
                    <span class="align-middle">{{ textEntity.display_label }}</span>
                    <small v-show="showFeedback(textEntity)" class="small">
                        <br />
                        {{ textEntity.feedback_message }}</small
                    >
                </div>
            </div>
            <div
                v-show="!reviewMode"
                class="tiny justify-content-end align-items-center position-relative mr-1"
                :class="{ 'd-flex': !reviewMode }"
                style="top: 6px; height: 18px"
            >
                <div class="flex-grow-1">
                    <div v-show="submitIsAnalyzing" class="ml-1">
                        <div class="d-flex">
                            <span class="font-weight-500 flex-grow-0 text-light-wave">{{ labelsTranslated.analyzingYourResponse }}...</span>
                        </div>
                    </div>
                </div>
                <div class="flex-grow-0 d-flex align-items-center position-relative">
                    <div v-if="panel.character_limit" class="mr-2">
                        <span class="text-secondary font-weight-500"
                            >({{ (textResponse || "").length }}/{{ panel.character_limit }})</span
                        >
                    </div>
                    <div v-show="linkedResponseLoading">
                        <div class="d-flex align-items-center">
                            <div class="spinner-border spinner-border-tiny text-primary ml-1 mr-2" role="status">
                                <span class="sr-only">Loading...</span>
                            </div>
                        </div>
                    </div>
                    <div v-show="responseSaving">
                        <div class="d-flex align-items-center text-light-wave">
                            <div class=" font-weight-500 ">{{ labelsTranslated.responseSaving }}...</div>
                        </div>
                    </div>
                    <div v-show="!responseSaving && textResponse">
                        <span
                            class="icon icon-glyphicons-basic-circle-check ml-1 mr-1 align-middle text-success"
                        ></span>
                        <span class="text-secondary font-weight-500 align-middle">{{
                            labelsTranslated.responseSaved
                        }}</span>
                    </div>
                </div>
            </div>
            <div v-if="savedResponseHasAiFeedback" class="mt-2">
                <div class="collapse" :id="'feedback-' + _uid" :class="{ show: forceShowFeedback }">
                    <div class="mt-3">
                        <text-response-ai-feedback :panel="panel" :response="savedResponse"></text-response-ai-feedback>
                    </div>
                    <div
                        v-if="savedResponse.expert_response"
                        class="alert alert-primary border-0 mb-0 mt-4"
                        ref="instructorResponse"
                    >
                        <div class="font-weight-500 mb-1">{{ labelsTranslated.compareYourAnswer }}:</div>
                        <div style="white-space: pre-wrap">{{ savedResponse.expert_response }}</div>
                    </div>
                </div>
            </div>
        </template>
    </base-panel>
</template>

<script>
import http from "@/axios.js"
import debounce from "lodash.debounce"
import BasePanel from "@/components/panels/BasePanel"
import TextResponseAiFeedback from "@/components/TextResponseAiFeedback"

export default {
    name: "TextResponsePanel",
    components: {
        BasePanel,
        TextResponseAiFeedback,
    },
    props: {
        value: {
            type: String,
            required: false,
        },
        panel: {
            type: Object,
            required: true,
        },
        sessionId: {
            type: [String, Number],
            required: false,
        },
        savedResponses: {
            type: Array,
            required: false,
        },
        locked: {
            type: Boolean,
            required: false,
        },
        reviewMode: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            textResponse: "",
            responseSaving: false,
            linkedResponseLoading: false,
            // Defining this throttled function here to create a unique 'copy' of the throttled function.
            // Otherwise, we run into problems when multiple instances of this component are shown simultaneously,
            // causing only the last component being typed into to be saved properly.
            saveResponseDebounced: debounce(
                async function () {
                    await this.saveUpdatedResponse()
                    this.responseSaving = false
                },
                600,
                { trailing: true }
            ),
            forceShowFeedback: false,
            // The index of the the response the learner is currently working on. This is used
            // to determine whether the current response has been submitted (if submitting is
            // required).
            currentResponseIndex: 0,
        }
    },
    computed: {
        panelID() {
            return this.panel.id
        },
        isSubmitted() {
            // The current response is considered submitted if it matches the last submitted response.
            return this.savedResponse?.is_submitted && this.textResponse === this.savedResponse.text
        },
        // The most recent saved response on the panel.
        savedResponse() {
            if (!this.savedResponses || this.savedResponses.length == 0) {
                return null
            }
            let savedResponse = this.savedResponses[this.savedResponses.length - 1]
            // This is a hack - if there are two responses, take the text response entities
            // from the first one.
            savedResponse.text_entities = this.savedResponses[0].text_entities
            return savedResponse
        },
        textResponseEntities() {
            if (!this.savedResponse) {
                return []
            }
            return this.savedResponse.text_entities
        },
        correctResponsesCount() {
            return this.textResponseEntities.filter((t) => t.was_discovered && t.correct).length
        },
        fillPanelHeight() {
            return !this.panel.textarea_rows
        },
        isDisabled() {
            return this.locked || (!this.panel.allow_answer_changing && this.isSubmitted)
        },
        labelsTranslated() {
            return this.$store.getters.sessionLabelsTranslated
        },
        submitIsAnalyzing() {
            return this.$store.getters.submitIsLoading
        },
        savedResponseHasAiFeedback() {
            return this.savedResponse && JSON.stringify(this.savedResponse.ai_gen_feedback) !== "{}"
        },
    },
    watch: {
        panelID() {
            this.initPanel()
        },
        isSubmitted(value) {
            this.$emit("update:isSubmitted", value)
            if (value) {
                this.$nextTick(() => {
                    $(`#feedback-${this._uid}`).collapse("show")
                })
            }
        },
        correctResponsesCount(value) {
            this.$emit("update:correctResponsesCount", value)
        },
        // This watcher is used when text response analysis is enabled. After the first time
        // the learner submits their response, the server returns metadata allowing the analysis
        // for keywords to happen in the front end. This provides more instant feedback to the learner
        // while editing their original response.
        textResponse(value) {
            if (!this.panel.analyze_text_responses || this.textResponseEntities.length == 0) {
                return
            }
            this.textResponseEntities.forEach((textResponseEntity) => {
                let keywordFound = false
                for (let i = 0; i < textResponseEntity.text_entity.keywords.length; i++) {
                    // Check for matches in keywords.
                    if (value.toLowerCase().includes(textResponseEntity.text_entity.keywords[i].value.toLowerCase())) {
                        textResponseEntity.was_discovered = true
                        keywordFound = true
                        break
                    }
                }
                if (!keywordFound) {
                    textResponseEntity.was_discovered = false
                }
            })
        },
        responseSaving(value) {
            this.$emit("update:isSavingResponse", value)
        },
    },
    methods: {
        async initPanel() {
            if (this.savedResponse) {
                this.textResponse = this.savedResponse.text
                this.$emit("input", this.textResponse)
                if (this.savedResponseHasAiFeedback) {
                    this.forceShowFeedback = true
                }
            } else if (this.panel.textarea_continue_from_prev && this.panel.linked_response_panel) {
                this.textResponse = await this.getPreviousResponse()
                this.saveUpdatedResponse()
                this.$emit("input", this.textResponse)
            }
            // Store the index of the response the learner is currently working on.
            this.currentResponseIndex = this.savedResponses.length
            this.$emit("update:isSubmitted", this.isSubmitted)
        },
        // Returns true if a text entity's feedback should be shown.
        showFeedback(textEntity) {
            if (textEntity.correct && !textEntity.was_discovered) {
                return true
            }
            if (textEntity.correct === false && textEntity.was_discovered) {
                return true
            }
            return false
        },
        // Returns true if a text entity should be hidden.
        hideEntity(textEntity) {
            if (!textEntity.correct && !textEntity.was_discovered) {
                return true
            }
            return false
        },
        saveResponse(response) {
            if (this.locked) {
                return
            }
            this.$emit("input", response)
            this.responseSaving = true
            this.saveResponseDebounced()
        },
        saveUpdatedResponse() {
            return new Promise((resolve) => {
                http.post(`/api/students/sessions/${this.sessionId}/panels/${this.panel.id}/text-response/`, {
                    textContent: this.textResponse,
                }).then(() => {
                    resolve()
                })
            })
        },
        // Get a previous response from a linked panel.
        getPreviousResponse() {
            this.linkedResponseLoading = true
            return new Promise((resolve) => {
                let vm = this
                http.get(
                    `/api/students/sessions/${this.sessionId}/panel/${this.panel.linked_response_panel}/responses/`
                )
                    .then(function (response) {
                        let previousResponses = response.data.responses
                        let previousResponse = previousResponses[vm.panel.linked_response_panel].text_responses[0]
                        if (!previousResponse) {
                            resolve("")
                            return
                        }
                        let text = previousResponse.text
                        resolve(text)
                    })
                    .finally(function () {
                        vm.linkedResponseLoading = false
                    })
            })
        },
        handleContinue() {
            this.$emit("continue")
        },
        handleSubmit() {
            this.$emit("submit")
        },
        handleEditAction(action) {
            this.$emit("edit-action", action)
        },
        handleUpdateWidth($event) {
            this.$emit("update-width", $event)
        },
        skipVideos() {
            this.$refs.basePanel.skipVideos()
        },
    },
    mounted() {
        this.initPanel()
    },
}
</script>

<style lang="scss" scoped>
.textarea {
    flex: 1 1 auto;
}
</style>